import { Drawer, Flex, Space } from '@billy/design-system'
import { ReactElement, ReactNode, useState } from 'react'

import { ImportedInvoiceDrawerActionsProvider } from '../../context/importedInvoiceDrawerActions'
import * as Styled from './styles'

interface ImportedInvoiceDrawerLayoutProps {
  children: ReactNode
  hasActions?: boolean
  status?: ReactNode
  title: string
}

/**
 * The header actions are not a prop: the content portals them into the slot here, through
 * `ImportedInvoiceDrawerActionsProvider`. `hasActions` only says whether to keep a slot for them.
 */
export const ImportedInvoiceDrawerLayout = ({
  children,
  hasActions = false,
  status,
  title,
}: ImportedInvoiceDrawerLayoutProps): ReactElement => {
  // State rather than a ref, so the content re-renders into the slot once it exists.
  const [actionsElement, setActionsElement] = useState<HTMLDivElement | null>(null)

  const isActionsSlotVisible = Boolean(status) || hasActions

  // `Drawer.Header` and `Drawer.Body` have to stay direct children — `Drawer` clones only those to
  // inject `closable` and `labelId`, so a wrapper would lose the close button and the dialog name.
  return (
    <ImportedInvoiceDrawerActionsProvider actionsElement={actionsElement}>
      <Drawer>
        <Drawer.Header title={title}>
          {status && <Styled.ImportedInvoiceDrawerHeaderStatus>{status}</Styled.ImportedInvoiceDrawerHeaderStatus>}
          {isActionsSlotVisible && (
            <>
              {/* Claims the free space; otherwise the close button's `margin-left: auto` would take
                  it and leave the actions next to the title. `Flex` doesn't forward refs, hence the inner slot. */}
              <Flex flexGrow={1} justifyContent="flex-end">
                <div ref={setActionsElement} />
              </Flex>
              <Space horizontal size="s8" />
            </>
          )}
        </Drawer.Header>
        <Drawer.Body>{children}</Drawer.Body>
      </Drawer>
    </ImportedInvoiceDrawerActionsProvider>
  )
}
